import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { ArrowLeft, Crown, Star } from "lucide-react";

const TIER_STYLES = {
  STANDARD: { ring: "border-[#ffd700]/40", label: "STANDARD" },
  DIAMOND: { ring: "border-[#ffd700]", label: "DIAMOND" },
  AMERICAN: { ring: "border-[#b22234] shadow-[0_0_30px_rgba(255,215,0,0.18)]", label: "AMERICAN" },
  MEGA: { ring: "border-[#ffd700] shadow-[0_0_40px_rgba(255,215,0,0.35)]", label: "MEGA" },
  CUSTOM: { ring: "border-white/40", label: "CUSTOM" },
  PATRIOT: { ring: "border-white/30", label: "PATRIOT" },
};

const FUNNY_LINES = [
  "🦅 just doubled it!",
  "🦅 went full American!",
  "🦅 mega-fueled the eagles!",
  "🦅 took the high road!",
  "🦅 unlocked legendary status!",
];

function lineFor(amount) {
  if (amount >= 400) return FUNNY_LINES[2];
  if (amount >= 250) return FUNNY_LINES[1];
  if (amount >= 200) return FUNNY_LINES[0];
  if (amount >= 150) return FUNNY_LINES[4];
  return FUNNY_LINES[3];
}

function timeLabel(min) {
  if (min < 1) return "just now";
  if (min === 1) return "1 minute ago";
  if (min < 60) return `${min} minutes ago`;
  const hr = Math.floor(min / 60);
  if (hr === 1) return "1 hour ago";
  if (hr < 24) return `${hr} hours ago`;
  const d = Math.floor(hr / 24);
  return d === 1 ? "1 day ago" : `${d} days ago`;
}

export default function Wall() {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    document.documentElement.classList.add("design-a");
    let stop = false;
    const load = async () => {
      try {
        const { data } = await api.get("/wall");
        if (!stop) setEntries(data.entries || []);
      } catch (e) {
        console.error(e);
      } finally {
        if (!stop) setLoading(false);
      }
    };
    load();
    const t = setInterval(load, 15000); // refresh every 15s
    return () => { stop = true; clearInterval(t); };
  }, []);

  return (
    <div className="design-a min-h-screen stars-bg">
      <div className="max-w-6xl mx-auto px-6 py-16 lg:py-24">
        <Link
          to="/"
          data-testid="wall-back-home"
          className="inline-flex items-center gap-2 font-accent text-[11px] tracking-[0.3em] text-white/60 hover:text-[#ffd700]"
        >
          <ArrowLeft size={12} /> Back to the Drive
        </Link>

        <div className="mt-8 mb-12">
          <span className="font-accent text-[11px] tracking-[0.4em] text-[#ffd700]">
            HALL OF GLORIOUS, ENTIRELY FICTIONAL VICTORY
          </span>
          <h1 className="mt-3 font-display text-6xl md:text-8xl font-black leading-[0.9]">
            <span className="gold-text">Patriot</span>{" "}
            <span className="text-white">Wall of</span>{" "}
            <span className="font-accent text-[#b22234] italic">Fame.</span>
          </h1>
          <p className="mt-5 max-w-2xl text-white/70 text-lg">
            The 25 most recent patriots who consented to be publicly celebrated
            for their gloriously imaginary contributions to nothing in particular.
          </p>
        </div>

        {loading && (
          <div className="text-center py-20">
            <div className="w-12 h-12 mx-auto border-4 border-[#ffd700]/30 border-t-[#ffd700] rounded-full animate-spin" />
            <p className="mt-4 font-accent text-[11px] tracking-[0.3em] text-white/50">
              LOADING THE HONORABLE
            </p>
          </div>
        )}

        {!loading && entries.length === 0 && (
          <div className="border border-[#ffd700]/30 bg-[#0a1628] p-12 text-center" data-testid="wall-empty">
            <Star size={48} className="mx-auto text-[#ffd700]/40 mb-4" />
            <h2 className="font-display text-3xl font-bold text-white/80">
              The wall is empty. For now.
            </h2>
            <p className="mt-3 text-white/50 max-w-md mx-auto">
              No patriots have opted in yet. Be the first by checking the{" "}
              <span className="text-[#ffd700]">"Add me to the Wall of Fame"</span>{" "}
              box on the donation form.
            </p>
            <Link
              to="/"
              className="mt-8 inline-flex items-center gap-2 bg-[#b22234] text-white font-accent text-[12px] tracking-[0.3em] px-6 py-3 border-2 border-[#ffd700] hover:bg-[#8b1827]"
            >
              CLAIM FIRST GLORY →
            </Link>
          </div>
        )}

        {!loading && entries.length > 0 && (
          <ul className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="wall-grid">
            {entries.map((e, i) => {
              const style = TIER_STYLES[e.tier] || TIER_STYLES.PATRIOT;
              const mega = e.tier === "MEGA";
              return (
                <li
                  key={i}
                  data-testid="wall-entry"
                  className={`relative bg-[#0a1628] border ${style.ring} p-6 hover:-translate-y-1 transition-transform`}
                >
                  {mega && (
                    <span className="absolute -top-3 left-4 bg-[#ffd700] text-[#050b14] font-accent text-[10px] tracking-[0.3em] px-2 py-1">
                      MEGA
                    </span>
                  )}
                  <div className="flex items-center justify-between mb-3">
                    <span className="font-accent text-[10px] tracking-[0.3em] text-[#ffd700]">
                      {style.label}
                    </span>
                    <Crown size={14} className="text-[#ffd700]/70" />
                  </div>
                  <div className="font-display text-3xl font-black text-white">
                    {e.first_name}
                  </div>
                  <div className="font-display text-5xl font-black gold-text mt-1">
                    ${e.amount.toFixed(0)}
                  </div>
                  <div className="mt-4 flex items-center justify-between text-[11px]">
                    <span className="text-white/70 italic">{lineFor(e.amount)}</span>
                    <span className="text-white/40 tracking-wider uppercase">
                      {timeLabel(e.minutes_ago)}
                    </span>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}
